import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for WebSocket connection
 * @param {string} url - WebSocket server URL
 * @param {Object} options - WebSocket connection options
 * @param {number} options.reconnectAttempts - Maximum number of reconnection attempts
 * @param {number} options.reconnectInterval - Base interval in milliseconds between reconnection attempts
 * @returns {Object} - WebSocket connection state and received messages
 */
const useWebSocket = (options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);
  const webSocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalDisconnectRef = useRef(false);

  // Default options
  const {
    reconnectAttempts = 5,
    reconnectInterval = 1000,
  } = options;

  // Reconnect to WebSocket server with exponential backoff
  const reconnect = useCallback(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Check if we've exceeded the maximum number of reconnection attempts
    if (reconnectAttemptsRef.current >= reconnectAttempts) {
      console.log(`Maximum reconnection attempts (${reconnectAttempts}) reached`);
      setError('Failed to reconnect after multiple attempts');
      reconnectAttemptsRef.current = 0;
      return;
    }

    // Calculate delay with exponential backoff
    const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current);
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${reconnectAttempts})`);

    // Set timeout for reconnection
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current += 1;
      connect();
    }, delay);
  }, [reconnectAttempts, reconnectInterval]);

  // Connect to WebSocket server
  const connect = useCallback(() => {
    try {
      // Reset intentional disconnect flag
      intentionalDisconnectRef.current = false;

      // Get user_id and organization_id from localStorage
      const userId = localStorage.getItem('CML_INSIGHTS_USER_ID');
      const organizationId = localStorage.getItem('CML_INSIGHTS_ORGANIZATION_ID');

      if (!userId || !organizationId) {
        setError('User ID or Organization ID not found');
        return;
      }

      // Create WebSocket URL with parameters
      const wsUrl = `${process.env.REACT_APP_CML_INSIGHTS_NOTIFICATION_WEB_SOCKET_URL}?user_id=${userId}&organization_id=${organizationId}`;
      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);

      // Set up event handlers
      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle notification messages
          if (message.type === 'notification') {
            setNotifications(prev => {
              // Check if notification with this ID already exists
              const exists = prev.some(notification => notification.id === message.data.id);
              // Only add if it doesn't exist
              return exists ? prev : [message.data, ...prev];
            });
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
        setIsConnected(false);
      };

      ws.onclose = (event) => {
        setIsConnected(false);

        // Attempt to reconnect if the disconnection wasn't intentional
        if (!intentionalDisconnectRef.current) {
          console.log('WebSocket connection closed unexpectedly. Attempting to reconnect...');
          reconnect();
        } else {
          console.log('WebSocket connection closed intentionally');
        }
      };

      // Store WebSocket instance in ref
      webSocketRef.current = ws;

      // Clean up function
      return () => {
        if (webSocketRef.current) {
          webSocketRef.current.close();
        }
      };
    } catch (err) {
      console.error('Error connecting to WebSocket:', err);
      setError(`Error connecting to WebSocket: ${err.message}`);
      setIsConnected(false);
    }
  }, [reconnect]);

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    // Set intentional disconnect flag to prevent auto-reconnect
    intentionalDisconnectRef.current = true;

    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnection attempts counter
    reconnectAttemptsRef.current = 0;

    // Close the WebSocket connection
    if (webSocketRef.current) {
      webSocketRef.current.close();
      webSocketRef.current = null;
    }
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      )
    );
  }, []);

  // Connect to WebSocket when component mounts
  useEffect(() => {
    const cleanup = connect();

    // Disconnect when component unmounts
    return () => {
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (cleanup) cleanup();
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    notifications,
    error,
    connect,
    disconnect,
    clearNotifications,
    markAsRead
  };
};

export default useWebSocket;
